import { DateTime } from "luxon";

import { api, ApiError } from "./api.js";
import type { EventRecord } from "./types.js";

/**
 * Which day of a repeating event you actually opened.
 *
 * An occurrence id carries its own start, and that is the one that matters:
 * tapping Thursday's hockey and being shown Tuesday's is confusing on its own,
 * and a "just this one" save would then write the wrong day. The detail screen
 * and the editor both need this, and they must not drift — so it lives here
 * rather than twice.
 */

export interface ResolvedOccurrence {
  start: DateTime;
  /** Null when the event has no end. All-day ends stay exclusive here. */
  end: DateTime | null;
  /** True when a specific occurrence was named and it is not the series start. */
  movedFromSeriesStart: boolean;
}

export function resolveOccurrence(
  event: EventRecord,
  occurrenceId: string | null,
  zone: string,
): ResolvedOccurrence {
  const seriesStart = DateTime.fromISO(event.starts_at, { zone });

  // An id looks like "<event>:<iso start>", so everything after the first
  // colon is the start. Slicing on the first colon keeps the ISO one intact.
  const stamp = occurrenceId === null ? null : occurrenceId.slice(occurrenceId.indexOf(":") + 1);
  const named = stamp === null ? null : DateTime.fromISO(stamp, { zone });
  const start = named?.isValid === true ? named : seriesStart;

  /*
   * Duration rather than the stored end: the series carries one end, and every
   * later occurrence is that same length starting at its own time. Copying the
   * stored end across would put every hockey practice's finish in August.
   */
  const durationMs =
    event.ends_at === null
      ? null
      : DateTime.fromISO(event.ends_at, { zone }).toMillis() - seriesStart.toMillis();

  return {
    start,
    end: durationMs === null ? null : start.plus({ milliseconds: durationMs }),
    movedFromSeriesStart: named?.isValid === true && !named.equals(seriesStart),
  };
}


/**
 * What to show for a given (event, occurrence) pair.
 *
 * Asks about the occurrence, because a "just this one" edit writes an
 * exception rather than touching the event — read the event back and you get
 * the series' title handed to someone who just changed that day's, which looks
 * exactly like a save that did not happen.
 *
 * Falls back to the event when the occurrence is not there. An occurrence id
 * is keyed on the start the rule produced, so moving a one-off event from 6am
 * to 8am leaves every link to its old 6am slot pointing at nothing — the
 * browser's back button and any bookmark included. The event is the honest
 * answer in that case, and for a one-off it is the same thing anyway.
 */
export async function loadOccurrenceOrEvent(
  eventId: string,
  occurrenceId: string | null,
): Promise<EventRecord> {
  if (occurrenceId !== null) {
    try {
      const o = await api.occurrence(eventId, occurrenceId);
      return {
        id: o.event_id,
        title: o.title,
        notes: o.notes,
        location: o.location,
        starts_at: o.starts_at,
        ends_at: o.ends_at,
        all_day: o.all_day,
        rrule: o.rrule,
        is_background: o.is_background,
        people: o.people,
      };
    } catch (error) {
      const gone = error instanceof ApiError && (error.status === 404 || error.status === 400);
      if (!gone) throw error;
    }
  }

  return api.event(eventId);
}
